import type { BleedingDay, FlowLevel, BloodColour } from "../../../types/gpSummary";
import Card from "./Card";

interface VaginalBleedingCardProps {
  bleedingLog: BleedingDay[];
}

// Match exact colours from daily bleeding log (ColourSectionCard)
const COLOUR_BG: Record<NonNullable<BloodColour>, string> = {
  bright_red: "#B2312C",
  dark_red: "#5A211E",
  brown: "#C8766B",
  yellowish: "#F5B9A6",
  clear: "#FFFFFF",
};

const COLOUR_LABELS: Record<NonNullable<BloodColour>, string> = {
  bright_red: "Bright red",
  dark_red: "Dark red",
  brown: "Brown",
  yellowish: "Yellowish",
  clear: "Clear",
};

// Flow-only fallback when colour is null but there's still flow
const FLOW_FALLBACK: Record<FlowLevel, string> = {
  heavy: "#E0A8A8",
  moderate: "#E8C4C4",
  mild: "#F0DADA",
  none: "#F0EDE8",
};

// Display labels for colour swatches used in heavy flow rows
const COLOUR_SWATCHES: Record<NonNullable<BloodColour>, { bg: string; label: string }> = {
  bright_red: { bg: "#B2312C", label: "Bright red" },
  dark_red: { bg: "#5A211E", label: "Dark red" },
  brown: { bg: "#C8766B", label: "Brown" },
  yellowish: { bg: "#F5B9A6", label: "Yellowish" },
  clear: { bg: "#FFFFFF", label: "Clear" },
};

function getSquareColor(d: BleedingDay): string {
  if (d.colour) return COLOUR_BG[d.colour];
  return FLOW_FALLBACK[d.flow];
}

export default function VaginalBleedingCard({ bleedingLog }: VaginalBleedingCardProps) {
  const bleedingDays = bleedingLog.filter((d) => d.flow !== "none");
  const heavyDays = bleedingLog.filter((d) => d.flow === "heavy");
  const lastBleeding = [...bleedingLog].reverse().find((d) => d.flow !== "none");
  const mediumClotDays = bleedingLog.filter((d) => d.clots === "medium");
  const largeClotDays = bleedingLog.filter((d) => d.clots === "large");
  const frequentClotDays = bleedingLog.filter((d) => d.clots === "frequent");
  const yellowDays = bleedingLog.filter((d) => d.colour === "yellowish");
  const lateBrightRed = bleedingLog.filter((d) => d.day > 14 && d.colour === "bright_red");

  const primaryClotDays = [...largeClotDays, ...frequentClotDays];
  const totalFlaggedClots = primaryClotDays.length + mediumClotDays.length;

  // Which colours actually appear in the data
  const presentColours = (["bright_red", "dark_red", "brown", "yellowish", "clear"] as NonNullable<BloodColour>[])
    .filter((c) => bleedingLog.some((d) => d.colour === c));

  return (
    <Card title="Vaginal Bleeding Summary">
      {/* Section 1: 42-Day Flow Timeline */}
      <div className="mb-6">
        <p className="text-xs font-semibold text-stone-500 mb-1">42-Day Flow Timeline</p>
        <p className="text-xs text-stone-400 mb-3">
          Each square = 1 day · Colour shows blood colour · Ring = clots reported
        </p>
        <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(7, 1fr)" }}>
          {bleedingLog.map((d) => {
            const hasClot = d.clots === "medium" || d.clots === "large" || d.clots === "frequent";
            const isSevere = d.clots === "large" || d.clots === "frequent";
            return (
              <div
                key={d.day}
                className="rounded-full aspect-square relative flex items-center justify-center"
                style={{
                  background: getSquareColor(d),
                  outline: hasClot ? `2px solid ${isSevere ? "#C97C7C" : "#E0A8A8"}` : "none",
                  outlineOffset: "-2px",
                  border: d.colour === "clear" ? "1px solid #e7e5e4" : "none",
                }}
                title={`Day ${d.day} (Wk ${d.week}): flow=${d.flow}${d.colour ? ` colour=${d.colour}` : ""} clots=${d.clots}`}
              >
                {(d.day - 1) % 7 === 0 && (
                  <span className="text-[7px] font-bold text-stone-400 absolute -top-3.5 left-0">
                    W{d.week}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {/* Colour legend */}
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4">
          {presentColours.map((c) => (
            <div key={c} className="flex items-center gap-1.5">
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{
                  background: COLOUR_BG[c],
                  border: c === "clear" ? "1px solid #e7e5e4" : "none",
                }}
              />
              <span className="text-xs text-stone-400">{COLOUR_LABELS[c]}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: "#F0EDE8" }} />
            <span className="text-xs text-stone-400">No bleeding</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span
              className="w-3 h-3 rounded-full flex-shrink-0 bg-stone-200"
              style={{ outline: "2px solid #C97C7C", outlineOffset: "-2px" }}
            />
            <span className="text-xs text-stone-400">Clots reported</span>
          </div>
        </div>
      </div>

      <div className="border-t border-stone-100 mb-6" />

      {/* Section 2: Duration Summary */}
      <div className="mb-6">
        <p className="text-xs font-semibold text-stone-500 mb-3">Duration Summary</p>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[
            {
              value: bleedingDays.length,
              label: "Days with bleeding",
              sublabel: "flow logged",
              bg: "#F5E8E8",
              color: "#C97C7C",
            },
            {
              value: heavyDays.length,
              label: "Heavy flow days",
              sublabel: "out of 42",
              bg: "#FBF3E0",
              color: "#a87f2a",
            },
            {
              value: lastBleeding ? `Day ${lastBleeding.day}` : "\u2014",
              label: "Last bleeding day",
              sublabel: `Week ${lastBleeding?.week ?? "\u2014"}`,
              bg: "#f0f6e4",
              color: "#6C7A52",
            },
          ].map((s) => (
            <div key={s.label} className="rounded-xl p-3 text-center" style={{ background: s.bg }}>
              <p className="text-lg font-bold leading-tight" style={{ color: s.color }}>
                {s.value}
              </p>
              <p className="text-xs text-stone-500 mt-0.5 leading-tight">{s.label}</p>
              <p className="text-xs text-stone-400 leading-tight">{s.sublabel}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-stone-100 mb-6" />

      {/* Section 3: Heavy Flow Days */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-stone-500">Heavy Flow Days</p>
          <span
            className="text-xs font-semibold px-2.5 py-0.5 rounded-full"
            style={{
              background: heavyDays.length > 0 ? "#F5E8E8" : "#f0f6e4",
              color: heavyDays.length > 0 ? "#C97C7C" : "#6C7A52",
            }}
          >
            {heavyDays.length} {heavyDays.length === 1 ? "day" : "days"}
          </span>
        </div>
        {heavyDays.length > 0 ? (
          <div className="flex flex-col gap-2">
            {heavyDays.map((d) => (
              <div
                key={d.day}
                className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-[#F5E8E8]"
              >
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#C97C7C] flex-shrink-0" />
                  <span className="text-sm font-semibold text-stone-700">Day {d.day}</span>
                  <span className="text-xs text-stone-400">{"\u00B7"} Week {d.week}</span>
                </div>
                {d.colour && (
                  <div className="flex items-center gap-1.5">
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0 border border-white/50"
                      style={{ background: COLOUR_SWATCHES[d.colour].bg }}
                    />
                    <span className="text-xs text-stone-500">
                      {COLOUR_SWATCHES[d.colour].label}
                    </span>
                  </div>
                )}
              </div>
            ))}
            {lateBrightRed.length > 0 && (
              <div className="flex gap-2 p-3 rounded-xl bg-[#FBF3E0] items-start mt-1">
                <span className="text-sm flex-shrink-0">{"\u26A0\uFE0F"}</span>
                <p className="text-xs leading-relaxed text-[#8B6320]">
                  Bright red bleeding was recorded after Day 14 (Day
                  {lateBrightRed.map((d) => ` ${d.day}`).join(",")}). Lochia typically darkens by
                  this point — worth discussing with your GP.
                </p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-stone-400 italic">No heavy flow days recorded.</p>
        )}
      </div>

      <div className="border-t border-stone-100 mb-6" />

      {/* Section 4: Clotting Flags */}
      <div className={yellowDays.length > 0 ? "mb-6" : ""}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-stone-500">Clotting Flags</p>
          <span
            className="text-xs font-semibold px-2.5 py-0.5 rounded-full"
            style={{
              background:
                primaryClotDays.length > 0
                  ? "#F5E8E8"
                  : mediumClotDays.length > 0
                    ? "#FBF3E0"
                    : "#f0f6e4",
              color:
                primaryClotDays.length > 0
                  ? "#C97C7C"
                  : mediumClotDays.length > 0
                    ? "#a87f2a"
                    : "#6C7A52",
            }}
          >
            {totalFlaggedClots > 0
              ? `${totalFlaggedClots} flagged`
              : "None flagged"}
          </span>
        </div>

        {primaryClotDays.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-[#C97C7C] mb-2">
              Large / frequent clots — primary concern
            </p>
            <div className="flex flex-col gap-2">
              {primaryClotDays.map((d) => (
                <div
                  key={`${d.day}-${d.clots}`}
                  className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-[#F5E8E8]"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{"\uD83E\uDE78"}</span>
                    <span className="text-sm font-semibold text-stone-700">Day {d.day}</span>
                    <span className="text-xs text-stone-400">{"\u00B7"} Week {d.week}</span>
                  </div>
                  <span className="text-xs font-semibold text-[#C97C7C]">
                    {d.clots === "frequent" ? "Frequent" : "Large"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {mediumClotDays.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-[#a87f2a] mb-2">
              Medium clots — worth mentioning
            </p>
            <div className="flex flex-col gap-2">
              {mediumClotDays.map((d) => (
                <div
                  key={d.day}
                  className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-[#FBF3E0]"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{"\uD83E\uDE78"}</span>
                    <span className="text-sm font-semibold text-stone-700">Day {d.day}</span>
                    <span className="text-xs text-stone-400">{"\u00B7"} Week {d.week}</span>
                  </div>
                  <span className="text-xs font-semibold text-[#a87f2a]">Medium (~50p)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {totalFlaggedClots === 0 ? (
          <div className="flex gap-2 p-3 rounded-xl bg-[#f0f6e4] items-start">
            <span className="text-sm flex-shrink-0">{"\u2705"}</span>
            <p className="text-xs leading-relaxed text-[#6C7A52]">
              No medium or large clots were reported. Small clots are normal in early postpartum and
              are not flagged here.
            </p>
          </div>
        ) : (
          <div className="flex gap-2 p-3 rounded-xl bg-[#F5E8E8] items-start">
            <span className="text-sm flex-shrink-0">{"\u26A0\uFE0F"}</span>
            <p className="text-xs leading-relaxed text-[#8B4040]">
              {primaryClotDays.length > 0
                ? `Large or frequent clots were reported on ${primaryClotDays.length} ${primaryClotDays.length === 1 ? "day" : "days"}. Please discuss with your GP — clots larger than a 50p coin may require review.`
                : `Medium clots (~50p coin) were reported on ${mediumClotDays.length} ${mediumClotDays.length === 1 ? "day" : "days"}. Mention these to your GP.`}
            </p>
          </div>
        )}
      </div>

      {/* Yellowish discharge warning */}
      {yellowDays.length > 0 && (
        <div>
          <div className="border-t border-stone-100 mb-6" />
          <div className="flex gap-2 p-3 rounded-xl bg-[#FBF3E0] items-start">
            <span className="text-sm flex-shrink-0">{"\u26A0\uFE0F"}</span>
            <p className="text-xs leading-relaxed text-[#8B6320]">
              Yellowish discharge was logged on Day
              {yellowDays.map((d) => ` ${d.day}`).join(",")} (Week {yellowDays[0].week}). This can
              sometimes indicate infection — please mention it to your GP.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}
